import useEmblaCarousel from "embla-carousel-react"
import { useEffect, useState } from "react"
import { useCallback } from "react"
import { AnimatePresence, motion } from "motion/react"
import { cn } from "src/utils/Functions"
import { Close, MusicNote, Pause, PlayArrow, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material"
import { AudioButton, useAudio } from "src/hooks/useAudio"
import { MiniAudioEqualizer } from "../MiniAudioEqualizer"
import { Button } from "@/components/ui/Button"
import { Tooltip, TooltipContent } from "@/components/ui/Tooltip"
import { Hourglass } from "src/assets/icons/Hourglass"
import { Checkbox } from "@/components/ui/Checkbox"
import { TooltipTrigger } from "@radix-ui/react-tooltip"


export const Slider = ({ children, className }: { children: React.ReactNode[], className?: string }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <div className="embla w-full max-w-[400px]" ref={emblaRef}>
        <div className="embla__container flex">
          {children.map((child, index) => (
            <div className="embla__slide flex-[0_0_100%] min-w-0" key={index}>
              {child}
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mx-auto">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={cn(
              "w-3 h-3 rounded-full transition-all duration-200",
              index === selectedIndex 
                ? "bg-white" 
                : "bg-white/30 hover:bg-white/50"
            )}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  )
}



export const TimeTermView = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
      <EQCard.Front>
        <div className="flex flex-col justify-between h-full overflow-hidden">

        <Slider className="mb-4">
        {data.cardUniqueData?.DURATION?.durationDetails?.map((item: any, index: number) => (
          <div key={index}>
            <h3 className="text-white/70 font-semibold mb-2">{item.title || `Duration Detail ${index + 1}`}</h3>
            <p className="text-white font-semibold">{item.description || item.title || item}</p>
          
              <div  className="w-full flex flex-row items-center justify-center gap-2 mt-24" key={index}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <div style={{ 
                    height: 64 + (index * 24),
                  }} 
                  className={cn("w-14 bg-neutral-900 rounded-full", {
                    "bg-linear-to-b from-[#3a8f6b] to-[#3a618f]": Math.max(0, index - (data.cardUniqueData?.DURATION?.durationDetails?.length || 0) + 1) <= 0,
                  })} 
                  key={index} />
                ))}
              </div>
            </div>
          ))}
        </Slider>



        </div>
      </EQCard.Front>
    </EQCard>
  )
}

// Percentage View
export const PercentageView = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  const ownershipData = data.cardUniqueData?.OWNERSHIP;

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
      <EQCard.Front>
        <div className="flex flex-col gap-12 h-full overflow-scroll scrollbar-hide">
          {ownershipData?.ownershipTerms?.map((item: any, index: number) => (
            <div key={index}>
              <h3 className="text-white/70 font-semibold mb-2">{item.title || `Ownership Term ${index + 1}`}</h3>
              <p className="text-white font-semibold text-xl mb-6">{item.description || item.title || JSON.stringify(item)}</p>

              <PercentBar value={parseFloat(item.description || '0') || 0} />
            </div>
          ))}
        </div>
      </EQCard.Front>
    </EQCard>
  )
}

export const PercentBar = ({ value }: { value: number }) => {
  return (
    <div className="w-full h-8 bg-neutral-900 rounded-full overflow-hidden">
      <div className="h-full bg-white/10 rounded-full bg-linear-to-r from-[#547bb5] to-[#6e53b8]" style={{ width: `${value}%` }}></div>
    </div>
  )
}


// Term View - Updated for v1 API model with union types
export const TermView = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  const moneyData = data.cardUniqueData?.MONEY_RECEIVED;

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
      <EQCard.Front>
        <div className="flex flex-col justify-between h-full">
          <div>
            <p className="text-white/70 font-semibold mb-2 text-base">{data.subTitle || "Total Advance"}</p>
            <p className="text-white font-semibold text-xl">{moneyData?.majorNumber}</p>
          </div>

          <ul className="flex flex-col gap-2 p-0 m-0">
            {moneyData?.paidAfterList?.map((item: string, index: number) => (
              <div className="flex justify-between border-b border-white/10 py-2 last:border-b-0" key={index}>
                <span className="text-white/70 text-sm font-semibold">{item}</span>
              </div>
            ))}
          </ul>
        </div>
      </EQCard.Front>
    </EQCard>
  )
}

// Ownership Details Card - Updated for v1 API model
export const OwnershipDetailsCard = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null)

  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
      <EQCard.Front>
        <div className="flex flex-col gap-4 h-full">
          <div className="flex flex-1 flex-col gap-4 w-full">
            <h3 className="text-white/70 font-semibold mb-2 text-base">{data.subTitle || "Ownership Details"}</h3>
          </div>
          <div className="flex flex-col items-center justify-center flex-auto auto gap-4 w-full h-full">
            {data.cardUniqueData?.OWNERSHIP?.ownershipTerms?.map((item: any, index: number) => (
              <div className="w-3/4 mx-auto" key={index}>
                <Tooltip open={openTooltipIndex === index} onOpenChange={(open) => setOpenTooltipIndex(open ? index : null)}>
                  <TooltipTrigger asChild>
                    <Button onClick={() => setOpenTooltipIndex(openTooltipIndex === index ? null : index)} className="w-full">{item.title || JSON.stringify(item)}</Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px]">
                    <p className="text-center">{item.description || item.title || JSON.stringify(item)}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </EQCard.Front>
  </EQCard>
  )
}

// 

// Term Length Details Card - Updated for v1 API model
export const TermLengthDetailsCard = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  return (
    <div className="relative">
      <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
        <EQCard.Front>
          <div className="flex flex-col gap-4 h-full">
            <h3 className="h-16 text-white/70 font-semibold mb-2">{data.subTitle || "Term Length Details"}</h3>
            <div className="flex flex-auto flex-col items-center justify-center gap-4">
              <div className="flex items-center justify-center gap-2 w-16 h-16 bg-linear-to-r from-[#547bb5] to-[#6e53b8] rounded-full">
                <Hourglass className="size-8 text-white" />
              </div>
              <div className="text-center mb-4">
                <p className="text-white font-semibold text-base">{data.cardUniqueData?.DURATION?.durationText}</p>
              </div>
              <Button className="w-3/4" onClick={() => setIsModalOpen(true)}>Open Term Details</Button> 
            </div>
          </div>
        </EQCard.Front>
      </EQCard>
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            onClick={() => setIsModalOpen(false)}
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", bounce: 0.2 }}
            className="p-8 bg-neutral-800 backdrop-blur-md w-[240px] h-[300px] absolute top-0 left-0 z-50 overflow-y-auto scrollbar-hide [mask-image:linear-gradient(to_bottom,transparent,black_5%,black_95%,transparent)] rounded-[3rem]"
          >
            <div className="flex items-center justify-between mb-0">
              <h1 className="text-white/70 font-semibold text-sm">{data.title}</h1>
              <Button variant="ghost" size="icon" onClick={() => setIsModalOpen(false)}>
                <Close className="size-6 text-white/70" />
              </Button>
            </div>
            <div className="flex flex-col gap-0">
              {data.cardUniqueData?.DURATION?.durationDetails?.map((item: any, index: number) => (
                <div className="py-4 border-b border-white/20 last:border-b-0" key={index}>
                  <p className="text-white/70 font-semibold mb-0 text-xs">Detail {index + 1}</p>
                  <p className="text-white font-semibold">{item.title ? `${item.title}: ${item.description}` : JSON.stringify(item)}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Album Card View - Updated for v1 API model
export const AlbumCardView = ({ data, selectable, onSelect: onSelectCard, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const responsibilities = data?.cardUniqueData?.RESPONSIBILITIES?.responsibilities || []

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelectCard} isSelected={isSelected}>
      <EQCard.Front>
        <div className="flex flex-col gap-0 h-full">
          <div>
            <p className="text-white/70 font-semibold mb-2 text-sm">{data.subTitle || "Delivery Requirements"}</p>
            <p className="text-white font-semibold text-base h-24">{responsibilities[selectedIndex]?.title ? `${responsibilities[selectedIndex]?.title}: ${responsibilities[selectedIndex]?.description}` : JSON.stringify(responsibilities[selectedIndex])}</p>
          </div>
          <div className="w-full max-w-[400px] overflow-hidden" ref={emblaRef}>
           <div className="flex gap-4">
              {responsibilities.map((item: any, index: number) => (
                <div key={index} className="flex-[0_0_70%] min-w-0 p-4 flex flex-col justify-between w-60 h-32 bg-linear-to-r from-[#547bb5] to-[#6e53b8] rounded-3xl">
                  <p className="text-[white/80] text-xs mb-8!">{item.title ? `${item.title}: ${item.description}` : JSON.stringify(item)}</p>
                  <div className="self-end">
                    <MusicNote className="size-8" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 mx-auto mt-4">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={cn(
                "size-2 rounded-full transition-all duration-200",
                index === selectedIndex 
                  ? "bg-white" 
                  : "bg-white/30 hover:bg-white/50"
              )}
              onClick={() => scrollTo(index)}
            />
          ))}
      </div>
        </div>
        
      </EQCard.Front>
    </EQCard>
  )
}

// Cost Card View - Updated for v1 API model
export const CostCardView = ({ data, selectable, onSelect, isSelected }: { data: any, selectable?: boolean, onSelect?: () => void, isSelected?: boolean }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  if (!data) {
    return <div className="w-[320px] h-[400px] bg-neutral-800 rounded-[3rem] flex items-center justify-center text-white/70">No data available</div>
  }

  // Create items array from v1 cardUniqueData union structure
  const legalData = data.cardUniqueData?.LEGAL;
  const items = [
    { title: "Risks", value: Array.isArray(legalData?.risks) ? legalData?.risks.join(', ') : legalData?.risks },
    { title: "Costs", value: Array.isArray(legalData?.costs) ? legalData?.costs.join(', ') : legalData?.costs },
    { title: "Legal", value: Array.isArray(legalData?.legal) ? legalData?.legal.join(', ') : legalData?.legal }
  ].filter(item => item.value) // Only show items that have values

  return (
    <EQCard audioSrc={data.ttsSrcUrl} selectable={selectable} onSelect={onSelect} isSelected={isSelected}>
      <EQCard.Front>
        <div>
          <h3 className="text-white/70 font-semibold mb-2 text-sm">{items[selectedIndex]?.title}</h3>
          <p className="text-white font-semibold text-base mb-6 h-32">{items[selectedIndex]?.value}</p>

          <div className="flex">
            {items.map((item: any, index: number) => (
              <div
                onClick={() => setSelectedIndex(index)}
                key={index}
                className={cn("absolute p-0.5  w-[60px] h-[60px] rounded-full bg-neutral-900 z-3 transition-all duration-300 bg-linear-to-b from-[#3a8f6b] to-[#3a618f]", {
                  "w-[130px] z-2": index === 1,
                  "w-[200px] z-1": index === 2,
                  
                })}
                >
                <div
                className={cn("px-8 flex w-full h-full items-center justify-center rounded-full bg-neutral-900", {
                  "bg-linear-to-b from-[#3a8f6b] to-[#3a618f]": index === selectedIndex,
                  "justify-end z-2": index === 1,
                  "justify-end z-1": index === 2,
                })}
                >
                 <p className="text-white/70 font-semibold mb-2 z-10 text-sm">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </EQCard.Front>
    </EQCard>
  )
}

export type EQCardProps = {
  children: React.ReactNode
  audioSrc: string
  selectable?: boolean
  onSelect?: () => void
  isSelected?: boolean
}

export const EQCard = ({ children, audioSrc, selectable = false, onSelect, isSelected = false }: EQCardProps) => {
  const [isFlipped, setIsFlipped] = useState(false)
  const { currentAudio, isPlaying } = useAudio()

  const isAudioPlaying = currentAudio === audioSrc && isPlaying

  return (
    <div 
      className={cn("border-2 border-transparent relative w-[240px] h-[300px] overflow-hidden group rounded-[3rem] hover:border-[#6e53b8]/50 transition-all duration-300", {
        "border-[#6e53b8]/50": isAudioPlaying,
      })} 
      style={{ 
        perspective: '1000px',
      }}
    >
      <div className="absolute top-8 right-8 z-10 flex items-center gap-2">
        {selectable && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => {
              if (checked !== isSelected) {
                onSelect?.()
              }
            }}
          />
        )}
        <AnimatePresence>
          <motion.div
            key={isAudioPlaying ? "audio-track" : "audio-track-exit"}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
          >
            {isAudioPlaying && <MiniAudioEqualizer />}
          </motion.div>
        </AnimatePresence>
        {audioSrc && (
        <AudioButton
          className="text-white/70 hover:text-white transition-colors"
          onClick={() => setIsFlipped(!isFlipped)}
          audioSrc={audioSrc}
        >
          <div className="relative w-6 h-6 z-10">
            <Pause
              fill="currentColor"
              className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${
                isAudioPlaying
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-75'
              }`}
            />
            <PlayArrow
              fill="currentColor"
              className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${
                isAudioPlaying
                  ? 'opacity-0 scale-75'
                  : 'opacity-100 scale-100'
              }`}
            />
          </div>
        </AudioButton>
        )}
       
      </div>
      <div 
        className="relative w-full h-full transition-transform duration-500"
        style={{ 
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {children}
      </div>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  )
}

EQCard.Front = ({ children }: { children: React.ReactNode }) => {
  return (
    <div 
      className="absolute inset-0 p-8 bg-neutral-800 rounded-[3rem] z-10"
      style={{ 
        backfaceVisibility: 'hidden',
      }}
    >
      {children}
    </div>
  )
}

EQCard.Back = ({ children }: { children: React.ReactNode }) => {
  return (
    <div 
      className="absolute inset-0 p-8 bg-neutral-800 rounded-[3rem]"
      style={{ 
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
      }}
    >
      {children}
    </div>
  )
}
